import { Space, Image } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import DeleteIcon from "@/assets/icons/DeleteIcon";
import EditIcon from "@/assets/icons/EditIcon";
import routes from "@/routes";
import { ProductType } from "@/utils/types";
import QRCode from "qrcode.react";
import NextIcon from "@/assets/icons/NextIcon";
import React from "react";
import C from "@/utils/constants";

const ItemCodeCell = React.memo(({ item, style }: any) => <span style={style}>{item}</span>);

export const columns = ({
  onDelete,
  onRestore,
  canEdit,
  canDelete,
  loadingList,
  titleBinCode,
  titlePosition,
}: {
  onDelete?: (id: string | number) => void;
  onRestore?: (id: string | number) => void;
  canEdit: boolean;
  canDelete: boolean;
  loadingList?: Array<string | number>;
  titleBinCode?: JSX.Element;
  titlePosition?: JSX.Element;
}) =>
  [
    {
      title: "Item Code",
      dataIndex: "identityCode",
      key: "identityCode",
      align: "center",
      width: 150,

      render: (_, record) => (
        <ItemCodeCell
          item={record?.masterProduct?.manufactureItemNumber}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <ItemCodeCell
          item={record?.productName}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (_, record) => (
        <ItemCodeCell
          item={record?.bin?.warehouse?.company?.companyName}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Warehouse Code",
      dataIndex: "wareHouseId",
      key: "wareHouseId",
      align: "center",
      width: 120,
      render: (_, record) => (
        <ItemCodeCell
          item={record?.bin?.warehouse?.identityCode}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: titleBinCode,
      dataIndex: "binCode",
      key: "binCode",
      align: "center",
      width: 120,
      render: (_, record) => (
        <ItemCodeCell
          item={record.bin.binCode}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: titlePosition,
      dataIndex: "position",
      key: "position",
      align: "center",
      width: 120,
      render: (_, record) => (
        <ItemCodeCell
          item={record.position}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "QR Code",
      dataIndex: "qrCode",
      key: "qrCode",
      align: "center",
      render: (_, record) => (
        <>
          <QRCode
            style={{ width: 45, height: 45 }}
            value={JSON.stringify({
              productId: record.id,
              companyId: record.bin.warehouse?.companyId,
            })}
          />
        </>
      ),
    },
    {
      title: "Current Count",
      dataIndex: "inventory",
      key: "inventory",
      align: "center",
      width: 100,
      render: (_, record) => (
        <ItemCodeCell
          item={record.inventory}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Max Par Level",
      dataIndex: "minQ",
      key: "minQ",
      align: "center",
      width: 100,
      render: (_, record) => (
        <ItemCodeCell
          item={record.minimumQuantity}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Reorder in Multiples of",
      dataIndex: "defaultReOrderQuantity",
      key: "defaultReOrderQuantity",
      width: 170,
      align: "center",
      render: (_, record) => (
        <ItemCodeCell
          item={record.defaultReOrderQuantity}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
          }}
        />
      ),
    },
    {
      title: "Product's image",
      dataIndex: "productImage",
      key: "productImage",
      align: "center",
      render: (_, record) => (
        <>
          {/* <QRCode`
            style={{ width: 45, height: 45 }}
            value={JSON.stringify({
              rfidCode: record.rfidCode?.toString(),
              companyId: record.bin.warehouse?.companyId,
            })}
          /> */} 
          <Image
            src={record.masterProduct?.images[0]?.imageUrl}
            style={{
              width: 40,
              opacity: record?.deletedAt ? 0.3 : 1,
            }}
            loading="eager"
            fallback={C.IMAGE_DEFAULT}
            preview={!record?.deletedAt}
          />
        </>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      align: "center",
      render: (_, record) => (
        <ItemCodeCell
          item={record.unit}
          style={{
            color: record?.deletedAt ? "#5B5959" : "#000000E0",
            maxWidth: 70,
            display: "block",
          }}
        />
      ),
    },
    {
      title: "Functions",
      key: "action",
      width: 150,
      align: "center",
      render: (_, record) => {
        const loading = !!loadingList?.find((item) => item === record?.id);
        return (
          <Space
            size="middle"
            style={{
              fontSize: 12,
            }}
          >
            {record?.deletedAt ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#192EEB",
                  cursor: "pointer",
                }}
                onClick={() => (loading ? undefined : onRestore?.(record?.id))}
              >
                <span>Restore</span>
                <NextIcon style={{ width: 16, cursor: "pointer" }} />
              </div>
            ) : (
              <>
                {canEdit && (
                  <Link to={routes.editSiteProduct.path.replace(":id", record.id)}>
                    <EditIcon style={{ width: 16, cursor: "pointer" }} />
                  </Link>
                )}
                {canDelete && (
                  <div onClick={() => (loading ? undefined : onDelete?.(record?.id))}>
                    <DeleteIcon style={{ width: 16, cursor: "pointer" }} />
                  </div>
                )}
                <Link to={routes.viewSiteProduct.path.replace(":id", record.id)}>
                  <NextIcon style={{ width: 16, cursor: "pointer" }} />
                </Link>
              </>
            )}
          </Space>
        );
      },
    },
  ] as ColumnsType<ProductType>;
